import { type Context, Controller } from '@hotwired/stimulus';
import { focusTrap } from '@primer/behaviors/dist/esm';
import { toggleHidden } from 'src/utils/dom-toggle';

const VALID_ROLES = ['dialog', 'alertdialog'];
const DIALOG_OPEN_BODY_CLASS = 'has-dialog';
const DIALOG_BACKDROP_CLASS = 'dialog-backdrop';

export default class DialogController extends Controller {
  public static values = {
    closeAction: String,
    positiveAction: String,
    negativeAction: String,
    neutralAction: String,
  };
  public static targets = [
    'backdrop',
    'modal',
    'closeButton',
    'positiveButton',
    'negativeButton',
    'neutralButton',
  ];

  declare hasBackdropTarget: boolean;
  declare hasModalTarget: boolean;

  declare private backdropTarget: HTMLElement;
  declare private modalTarget: HTMLElement;
  declare private closeButtonTargets: HTMLButtonElement[];
  declare private positiveButtonTargets: HTMLButtonElement[];
  declare private negativeButtonTargets: HTMLButtonElement[];
  declare private neutralButtonTargets: HTMLButtonElement[];

  declare private hasCloseActionValue: boolean;
  declare private hasPositiveActionValue: boolean;
  declare private hasNegativeActionValue: boolean;
  declare private hasNeutralActionValue: boolean;

  declare private closeActionValue: string;
  declare private positiveActionValue: string;
  declare private negativeActionValue: string;
  declare private neutralActionValue: string;

  public focusAfterClosed!: HTMLElement;

  private backdropNode!: Element;
  private focusFirst!: HTMLElement;
  private focusTrapController!: AbortController;

  constructor(context: Context) {
    super(context);
  }

  connect(): void {
    // Protect the programmer by ensuring the role is an accessible one.
    const role = this.element.getAttribute('role') || '';
    const isDialog = role
      .trim()
      .split(/\s+/g)
      .some((token) => VALID_ROLES.indexOf(token) !== -1);

    if (!isDialog) {
      throw new Error(
        'Dialog() requires a DOM element with ARIA role: ' +
          VALID_ROLES.join(', '),
      );
    }

    // Wrap in an individual backdrop element if one doesn't exist
    // Native <dialog> elements use the ::backdrop pseudo-element, which
    // works similarly.
    if (
      this.element.parentNode instanceof Element &&
      this.element.parentNode.classList.contains(DIALOG_BACKDROP_CLASS)
    ) {
      this.backdropNode = this.element.parentNode;
    } else if (this.hasBackdropTarget) {
      this.backdropNode = this.backdropTarget;
    } else if (this.element.querySelector(`.${DIALOG_BACKDROP_CLASS}`)) {
      this.backdropNode = this.element.querySelector(
        `.${DIALOG_BACKDROP_CLASS}`,
      )!;
      this.backdropNode.setAttribute('data-dialog-target', 'backdrop');
    } else {
      this.backdropNode = document.createElement('div');
      this.backdropNode.className = DIALOG_BACKDROP_CLASS;
      this.element.insertBefore(this.backdropNode, this.element);
      this.backdropNode.appendChild(this.element);
      this.backdropNode.setAttribute('data-dialog-target', 'backdrop');
    }

    // Click seems the right candidate, but it's not. A click is registered on
    // mouseup, and that happens when selecting content on the screen and
    // releasing. The release point will be clicked, even when the mousedown
    // was inside the dialog.
    //
    // Especially when selecting text, this leads to unwanted "backdrop" events.
    if (this.hasCloseActionValue) {
      this.backdropNode.setAttribute(
        'data-action',
        this.closeActionValue
          .split(' ')
          .map((action) => `mousedown->${action.split('->').pop()}`)
          .join(' '),
      );
    } else if (this.closeButtonTargets.length > 0) {
      this.backdropNode.setAttribute(
        'data-action',
        'mousedown->dialog#onClose',
      );
    }

    // Ensure turbo doesn't cache these
    // if (!this.element.hasAttribute('data-turbo-not-temporary')) {
    //   this.element.setAttribute('data-turbo-temporary', '');
    // }

    // Upgrade buttons (no downgrade)
    this.setButtonActions(
      this.closeButtonTargets,
      this.hasCloseActionValue ? this.closeActionValue : 'dialog#onClose',
    );
    this.setButtonActions(
      this.neutralButtonTargets,
      this.hasNeutralActionValue ? this.neutralActionValue : 'dialog#onClose',
    );
    this.setButtonActions(
      this.negativeButtonTargets,
      this.hasNegativeActionValue ? this.negativeActionValue : 'dialog#onClose',
    );
    this.setButtonActions(
      this.positiveButtonTargets,
      this.hasPositiveActionValue ? this.positiveActionValue : 'dialog#onClose',
    );
  }

  disconnect(): void {
    // Upgrade is idempotent so no need to downgrade
  }

  public onClose(event: Event) {
    if (!this.active) {
      return;
    }

    // e.g.: clicking an autocomplete option in a datalist
    if (
      (event instanceof KeyboardEvent || event.type === 'keydown') &&
      (!('code' in event) || !event.code)
    ) {
      return;
    }

    this.close();
  }

  public onOpen(event: Event) {
    if (
      !('detail' in event) ||
      typeof event.detail !== 'object' ||
      !event.detail
    ) {
      throw new Error('Expected custom event with detail property');
    }

    const stimulusAction = event as Omit<CustomEvent, 'detail'> & {
      detail: {
        dialog: {
          focusAfterClosed: string;
          focusFirst?: string;
          clearOnOpen?: boolean;
        };
      };
    };
    if (
      !('dialog' in stimulusAction.detail) ||
      !('focusAfterClosed' in stimulusAction.detail.dialog)
    ) {
      throw new Error(
        `Expected dialog in detail, and focusAfterClosed in dialog, actual: ${Object.keys(
          stimulusAction.detail,
        ).toString()} and dialog: ${stimulusAction.detail.dialog ? Object.keys(stimulusAction.detail.dialog).toString() : '<missing>'}`,
      );
    }

    const { focusAfterClosed, focusFirst, clearOnOpen } =
      stimulusAction.detail.dialog;
    this.open(focusAfterClosed, focusFirst, clearOnOpen);
  }

  public open(
    focusAfterClosed?: string | Node,
    focusFirst?: string | Node,
    clearOnOpen: boolean = false,
  ) {
    // Get the item to focus when this dialog is closed. This is usually the
    // source of opening this dialog, such as a button.
    if (typeof focusAfterClosed === 'string') {
      this.focusAfterClosed =
        document.getElementById(focusAfterClosed) ||
        document.querySelector(focusAfterClosed)!;
    } else if (focusAfterClosed instanceof HTMLElement) {
      this.focusAfterClosed = focusAfterClosed;
    } else if (
      focusAfterClosed === undefined &&
      this.focusAfterClosed === undefined
    ) {
      throw new Error('the focusAfterClosed parameter is required.');
    }

    // Get the item to focus when this dialog is opened.
    if (typeof focusFirst === 'string') {
      this.focusFirst =
        document.getElementById(focusFirst) ||
        document.querySelector(focusFirst)!;
    } else if (focusFirst instanceof HTMLElement) {
      this.focusFirst = focusFirst;
    }

    if (this.active) {
      return;
    }

    if (clearOnOpen) {
      this.clear();
    }
    this.active = true;
  }

  public close() {
    if (!this.active) {
      return;
    }

    const event = this.dispatch('close', {
      cancelable: true,
    });

    if (event.defaultPrevented) {
      return;
    }

    this.active = false;
  }

  public focus() {
    const initialFocus =
      this.focusFirst ||
      this.element.querySelector<HTMLElement>('[data-autofocus]');

    const focusTrapTarget = this.hasModalTarget
      ? this.modalTarget
      : (this.element as HTMLElement);

    this.focusTrapController = new AbortController();
    focusTrap(focusTrapTarget, initialFocus, this.focusTrapController.signal);
  }

  public blur() {
    this.focusTrapController.abort('DialogController#blur');
  }

  public clear(): void {
    this.element.querySelectorAll('input').forEach((input) => {
      const type = input.getAttribute('type');
      if (type === 'hidden' || type === 'submit') {
        return;
      }

      if (type === 'checkbox' || type === 'radio') {
        input.checked = false;
        return;
      }

      input.value = '';
    });

    this.element.querySelectorAll('textarea').forEach((textarea) => {
      textarea.value = '';
    });
  }

  public get active(): boolean {
    return !this.element.hasAttribute('hidden');
  }

  public set active(next: boolean) {
    if (this.active === next) {
      return;
    }

    if (next) {
      toggleHidden(this.element, false);

      // If not already showing
      if (!this.backdropNode.classList.contains('active')) {
        this.backdropNode.classList.remove(
          'ease-in',
          'duration-200',
          'opacity-100',
          'pointer-events-none',
        );

        this.backdropNode.classList.add(
          'ease-out',
          'duration-300',
          'opacity-0',
          'active',
        );

        if (this.hasModalTarget) {
          this.modalTarget.classList.remove(
            'ease-in',
            'duration-200',
            'opacity-100',
            'translate-y-0',
            'sm:scale-100',
            'pointer-events-none',
          );

          this.modalTarget.classList.add(
            'ease-out',
            'duration-300',
            'opacity-0',
            'translate-y-4',
            'sm:translate-y-0',
            'sm:scale-95',
          );
        }

        requestAnimationFrame(() => {
          // Closed already
          if (!this.backdropNode.classList.contains('active')) {
            return;
          }

          this.backdropNode.classList.remove('opacity-0');
          this.backdropNode.classList.add('opacity-100');

          if (this.hasModalTarget) {
            this.modalTarget.classList.remove(
              'opacity-0',
              'translate-y-4',
              'sm:translate-y-0',
              'sm:scale-95',
            );

            this.modalTarget.classList.add(
              'opacity-100',
              'translate-y-0',
              'sm:scale-100',
            );
          }
        });
      }

      addActiveDialog(this);
    } else {
      if (this.backdropNode.classList.contains('active')) {
        this.backdropNode.classList.remove(
          'ease-out',
          'duration-300',
          'opacity-0',
          'active',
        );
        this.backdropNode.classList.add(
          'ease-in',
          'duration-200',
          'opacity-100',
          'pointer-events-none',
        );

        if (this.hasModalTarget) {
          this.modalTarget.classList.remove(
            'ease-out',
            'duration-300',
            'opacity-0',
            'translate-y-4',
            'sm:translate-y-0',
            'sm:scale-95',
          );

          this.modalTarget.classList.add(
            'ease-in',
            'duration-200',
            'opacity-100',
            'translate-y-0',
            'sm:scale-100',
            'pointer-events-none',
          );
        }

        requestAnimationFrame(() => {
          // Made visible again
          if (this.backdropNode.classList.contains('active')) {
            return;
          }

          this.backdropNode.classList.remove('opacity-100');
          this.backdropNode.classList.add('opacity-0');

          if (this.hasModalTarget) {
            this.modalTarget.classList.remove(
              'opacity-100',
              'translate-y-0',
              'sm:scale-100',
            );

            this.modalTarget.classList.add(
              'opacity-0',
              'translate-y-4',
              'sm:translate-y-0',
              'sm:scale-95',
            );
          }

          setTimeout(() => {
            // Made visible again
            if (this.backdropNode.classList.contains('active')) {
              return;
            }

            toggleHidden(this.element, true);
          }, 200);
        });
      } else {
        toggleHidden(this.element, true);
      }

      removeActiveDialog(this);
    }
  }

  private setButtonActions(buttonList: HTMLElement[], action: string) {
    buttonList.forEach((button) => {
      if (button.hasAttribute('data-action')) {
        return;
      }

      button.setAttribute('data-action', action);
    });
  }
}

const ACTIVE_DIALOGS: DialogController[] = [];
function addActiveDialog(dialog: DialogController) {
  document.body.classList.add(DIALOG_OPEN_BODY_CLASS);
  ACTIVE_DIALOGS.unshift(dialog);

  if (ACTIVE_DIALOGS.length > 1) {
    ACTIVE_DIALOGS[1].blur();
  }

  dialog.focus();
}

function removeActiveDialog(dialog: DialogController) {
  const index = ACTIVE_DIALOGS.indexOf(dialog);
  if (index === -1) {
    return;
  }

  if (index === 0) {
    ACTIVE_DIALOGS[0].blur();

    dialog.focusAfterClosed.focus();
  }

  ACTIVE_DIALOGS.splice(index, 1);

  if (ACTIVE_DIALOGS.length === 0) {
    // Last to remove
    document.body.classList.remove(DIALOG_OPEN_BODY_CLASS);
  } else {
    // Activate the top one on the stack
    ACTIVE_DIALOGS[0].focus();
  }
}
